import { useEffect, useState, useTransition } from "react";
import baseStyles from "../BaseInputs.module.css";

interface BaseSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  id: string;
  value: string | number | undefined;
  availableOptions: {
    label: string;
    value: string | number | boolean | undefined;
    disabled?: boolean;
  }[];
  change: (ret: string | number | boolean | undefined) => void;
  label?: string;
}

/**
 * Generic select input used by the ObjectEditor to pick from options.
 *
 * @param props - Destructured parameter object
 * @param props.id - Identifier applied to the `select` element and its label.
 * @param props.value - Currently selected value (may be `string`, `number`, or `undefined`).
 * @param props.availableOptions - Array of option objects with `label`, `value` and optional `disabled`.
 * @param props.change - Callback invoked with the selected option's raw value when selection changes.
 * @param props.label - Optional label text displayed above the select element.
 */
export const BaseSelect = ({
  id,
  value,
  availableOptions,
  change,
  label,
  ...rest
}: BaseSelectProps): React.ReactElement => {
  const [_isPending, startTransition] = useTransition();
  const [thisValueString, setThisValueString] = useState(`${value}`);
  useEffect(() => {
    const ix = availableOptions.findIndex((o) => o.value === value);
    startTransition(() => setThisValueString(`${ix !== -1 ? `${ix}-` : ""}${value}`));
  }, [availableOptions, value]);

  const availableOptionsStrings = availableOptions.map((item, ix) => ({
    label: item.label,
    value: `${ix}-${item.value?.toString() ?? "undefined"}`,
    disabled: item.disabled,
  }));

  /**
   * Handle selection changes, update internal string representation and
   * propagate the selected option value via the `change` callback.
   *
   * @param e - The change event from the select element.
   */
  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    e.preventDefault();
    e.stopPropagation();
    const newValueIx = availableOptionsStrings.findIndex((item) => item.value === e.target.value);
    setThisValueString(e.target.value);
    /* istanbul ignore else */
    if (newValueIx !== -1) {
      change(availableOptions[newValueIx].value);
    }
  };

  return (
    <div className={baseStyles.holder}>
      {label !== undefined && (
        <label
          className={baseStyles.label}
          id={`${id}-label`}
          htmlFor={id}
        >
          {label}
        </label>
      )}
      <select
        {...rest}
        id={id}
        className={baseStyles.baseSelect}
        value={thisValueString}
        onChange={handleChange}
      >
        {availableOptions.findIndex((o) => o.value === value) === -1 && (
          <option
            value={thisValueString}
            disabled
          >
            {thisValueString}
          </option>
        )}
        {availableOptionsStrings.map((o, ix) => (
          <option
            key={`${ix}-${o.value}`}
            value={o.value}
            disabled={o.disabled}
          >
            {o.label}
          </option>
        ))}
      </select>
    </div>
  );
};

BaseSelect.displayName = "BaseSelect";
